import type { Vehicle } from '../sim/vehicle';

/** Snapshot of the vehicle's renderable state at one sim step, for interpolated rendering. */
export interface PoseSnapshot {
  px: number;
  py: number;
  pz: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  /** Per wheel: suspension cast distance, spin angle, steer angle. */
  wheelDist: Float64Array;
  wheelSpin: Float64Array;
  wheelSteer: Float64Array;
}

export const makeSnapshot = (): PoseSnapshot => ({
  px: 0,
  py: 0,
  pz: 0,
  qx: 0,
  qy: 0,
  qz: 0,
  qw: 1,
  wheelDist: new Float64Array(4),
  wheelSpin: new Float64Array(4),
  wheelSteer: new Float64Array(4),
});

export function capture(out: PoseSnapshot, v: Vehicle): void {
  const t = v.body.translation();
  const r = v.body.rotation();
  out.px = t.x;
  out.py = t.y;
  out.pz = t.z;
  out.qx = r.x;
  out.qy = r.y;
  out.qz = r.z;
  out.qw = r.w;
  for (let i = 0; i < 4; i++) {
    const w = v.wheels[i];
    out.wheelDist[i] = w.castDistance;
    out.wheelSpin[i] = w.angle;
    out.wheelSteer[i] = w.steer;
  }
}

export function copySnapshot(out: PoseSnapshot, a: PoseSnapshot): void {
  out.px = a.px;
  out.py = a.py;
  out.pz = a.pz;
  out.qx = a.qx;
  out.qy = a.qy;
  out.qz = a.qz;
  out.qw = a.qw;
  out.wheelDist.set(a.wheelDist);
  out.wheelSpin.set(a.wheelSpin);
  out.wheelSteer.set(a.wheelSteer);
}

/** Interpolate (nlerp for rotation). */
export function lerpSnapshot(out: PoseSnapshot, a: PoseSnapshot, b: PoseSnapshot, t: number): void {
  out.px = a.px + (b.px - a.px) * t;
  out.py = a.py + (b.py - a.py) * t;
  out.pz = a.pz + (b.pz - a.pz) * t;
  let bx = b.qx;
  let by = b.qy;
  let bz = b.qz;
  let bw = b.qw;
  if (a.qx * bx + a.qy * by + a.qz * bz + a.qw * bw < 0) {
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }
  let x = a.qx + (bx - a.qx) * t;
  let y = a.qy + (by - a.qy) * t;
  let z = a.qz + (bz - a.qz) * t;
  let w = a.qw + (bw - a.qw) * t;
  const l = Math.hypot(x, y, z, w) || 1;
  x /= l;
  y /= l;
  z /= l;
  w /= l;
  out.qx = x;
  out.qy = y;
  out.qz = z;
  out.qw = w;
  for (let i = 0; i < 4; i++) {
    out.wheelDist[i] = a.wheelDist[i] + (b.wheelDist[i] - a.wheelDist[i]) * t;
    out.wheelSpin[i] = a.wheelSpin[i] + (b.wheelSpin[i] - a.wheelSpin[i]) * t;
    out.wheelSteer[i] = a.wheelSteer[i] + (b.wheelSteer[i] - a.wheelSteer[i]) * t;
  }
}
